import type { LiveSubscription, SavedPaymentMethod } from "@pax/api-contract/payments/v1alpha1";
import {
  Button,
  cn,
  focusHaloFlush,
  HapticSurface,
  panelCard,
  panelMotion,
  panelRowQuiet,
  Skeleton,
} from "@pax/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronRight } from "lucide-react";
import { useState } from "react";
import { useBillingPortal } from "@/features/credits/billing-portal";
import { Coin } from "@/features/credits/components/Coin";
import { RemovePaymentMethodDialog } from "@/features/credits/components/SavedPaymentMethodSection";
import { creditsOf, formatCredits, formatMoney, thresholdCredits } from "@/features/credits/format";
import { creditBalanceQuery, offersQuery, topUpSettingsQuery } from "@/features/credits/queries";
import { useTopUp } from "@/features/credits/top-up";
import { api, orpc, reachable, requestErrorMessage } from "@/lib/api";
import { startCardVerification } from "@/lib/risk";
import { BalancePage } from "./BalancePage";
import { ExternalButtonRow, Group, quietButton, SettingRow, SwitchSettingRow } from "./parts";
import { useSubPageMove } from "./sub-page";

const subscriptionsQuery = orpc.payments.subscription.get.queryOptions({
  select: ({ tokenSubscriptions, patronSubscriptions, stripeBilling }) => ({
    live: [...tokenSubscriptions, ...patronSubscriptions],
    stripeBilling,
  }),
});

/**
 * Everything about paying for Pax: the balance, with what it's made of and how it changed one level
 * down, subscriptions, automatic top-up, and the saved card. Stripe's own page handles cancelling
 * and receipts.
 */
export function BillingSection({ userId }: { userId: string }) {
  const [page, setPage] = useState<"billing" | "balance">("billing");
  const prepare = useSubPageMove();
  const { portal, openBilling } = useBillingPortal();
  const { data } = useQuery(subscriptionsQuery);
  const subscriptions = data?.live;
  const onWeb = subscriptions?.some(subscription => subscription.provider === "stripe") ?? false;

  if (page === "balance") return <BalancePage userId={userId} onBack={() => setPage("billing")} />;

  return (
    <div className="flex flex-col gap-6">
      <BalanceCard
        userId={userId}
        onOpen={() => {
          prepare();
          setPage("balance");
        }}
      />
      <Subscriptions
        subscriptions={subscriptions}
        portalBusy={portal.isPending}
        onManage={openBilling}
      />
      <AutoTopUp />
      <PaymentMethod />
      {/* Stripe's billing page has nothing to show an account that has never paid on the web. */}
      {data?.stripeBilling ? (
        <Group
          note={
            portal.error ? (
              <span role="alert" className="text-destructive">
                {requestErrorMessage(portal.error)}
              </span>
            ) : undefined
          }
        >
          <ExternalButtonRow
            label="Invoices and receipts"
            disabled={portal.isPending}
            onPress={openBilling}
          />
          {onWeb ? (
            <ExternalButtonRow
              label="Manage web subscription"
              disabled={portal.isPending}
              onPress={openBilling}
            />
          ) : null}
        </Group>
      ) : null}
    </div>
  );
}

/** The balance at the top of Billing, which opens the Balance page, and the way to buy more. */
function BalanceCard({ userId, onOpen }: { userId: string; onOpen: () => void }) {
  const { openTopUp } = useTopUp();
  const { data: credits } = useQuery(creditBalanceQuery(userId));
  return (
    <div className={cn("flex items-center gap-2 p-1 pr-3", panelCard)}>
      <button
        type="button"
        onClick={onOpen}
        className={cn(
          "relative flex min-w-0 flex-1 items-center rounded-lg p-3 text-left hover:bg-foreground/5",
          panelMotion,
          focusHaloFlush,
        )}
      >
        <span className="flex min-w-0 items-center gap-2">
          <Coin className="size-7 shrink-0" />
          {credits === undefined ? (
            <span className="my-1 block h-7 w-20 animate-pulse rounded-md bg-muted" />
          ) : (
            <span className="text-3xl font-semibold tracking-tight tabular-nums">
              {formatCredits(credits)}
              <span className="sr-only"> credits</span>
            </span>
          )}
          <ChevronRight aria-hidden className="size-5 shrink-0 text-muted-foreground" />
        </span>
        <HapticSurface />
      </button>
      <Button className="shrink-0 rounded-full" onClick={() => openTopUp("pack")}>
        Buy credits
      </Button>
    </div>
  );
}

/**
 * Each subscription by name. Where it runs is only said when one runs on an iPhone, since App Store
 * subscriptions can only be changed there.
 */
function Subscriptions({
  subscriptions,
  portalBusy,
  onManage,
}: {
  subscriptions: LiveSubscription[] | undefined;
  portalBusy: boolean;
  onManage: () => void;
}) {
  const { openTopUp } = useTopUp();
  const { data: offers } = useQuery(offersQuery);
  const sayWhere = subscriptions?.some(subscription => subscription.provider === "app_store");

  return (
    <Group label="Subscriptions">
      {!subscriptions || !offers ? (
        <div className={cn(panelRowQuiet, "h-auto min-h-11 py-2")}>
          <Skeleton className="h-4 w-32" />
        </div>
      ) : subscriptions.length === 0 ? (
        <SettingRow title="No subscription" muted>
          <button
            type="button"
            className={quietButton}
            onClick={() => openTopUp("token_subscription")}
          >
            See plans
          </button>
        </SettingRow>
      ) : (
        subscriptions.map(subscription => {
          const offer = offers.find(item => item.offerId === subscription.offerId);
          const amount = !offer
            ? null
            : offer.kind === "patron"
              ? `${formatMoney(offer.priceCents)} a month`
              : `${creditsOf(offer).toLocaleString()} credits a month`;
          const where = subscription.provider === "app_store" ? "iPhone" : "Web";
          const patron = offer?.kind === "patron";
          return (
            <SettingRow
              key={subscription.subscriptionId}
              title={offer?.name ?? "Subscription"}
              description={[amount, sayWhere ? where : null].filter(Boolean).join(" · ")}
            >
              {subscription.provider === "app_store" ? (
                <span className="text-right text-xs text-muted-foreground">
                  Managed in iPhone Settings
                </span>
              ) : patron ? (
                <button
                  type="button"
                  disabled={portalBusy}
                  className={quietButton}
                  onClick={onManage}
                >
                  Manage
                </button>
              ) : (
                <button
                  type="button"
                  className={quietButton}
                  onClick={() => openTopUp("token_subscription")}
                >
                  Change
                </button>
              )}
            </SettingRow>
          );
        })
      )}
    </Group>
  );
}

/**
 * Automatic top-up. Off, it offers to set it up where packs are bought; on, it is a switch that says
 * what it buys, and turning the switch off deletes the rule.
 */
function AutoTopUp() {
  const queryClient = useQueryClient();
  const { openTopUp } = useTopUp();
  const { data: settings } = useQuery(topUpSettingsQuery);
  const { data: offers } = useQuery(offersQuery);
  const turnOff = useMutation({
    mutationFn: () => reachable(api.payments.autoTopUp.delete()),
    onSuccess: updated => queryClient.setQueryData(topUpSettingsQuery.queryKey, updated),
  });
  if (!settings) return null;
  const pack = offers?.find(offer => offer.offerId === settings.autoOfferId)?.name ?? "a pack";
  const threshold = settings.autoThresholdRaw ? thresholdCredits(settings.autoThresholdRaw) : "";
  const description = {
    off: "",
    on: `Buys ${pack} below ${threshold} credits`,
    charging: `Buying ${pack}…`,
    needs_bank: "Paused until your bank confirms",
    needs_address: "Paused until Stripe has your billing address",
    declined: "Paused after a payment failed",
  }[settings.autoState];

  return (
    <Group
      label="Auto top-up"
      note={
        turnOff.error ? (
          <span role="alert" className="text-destructive">
            {requestErrorMessage(turnOff.error)}
          </span>
        ) : undefined
      }
    >
      {settings.autoState === "off" ? (
        <SettingRow title="Top up when low">
          <button type="button" className={quietButton} onClick={() => openTopUp("pack")}>
            Set up
          </button>
        </SettingRow>
      ) : (
        <SwitchSettingRow
          title="Top up when low"
          description={description}
          checked
          disabled={turnOff.isPending || settings.autoState === "charging"}
          onChange={() => turnOff.mutate()}
        />
      )}
    </Group>
  );
}

/**
 * The saved payment method, drawn as a card, and the way to remove it. Without one, Add card opens
 * Stripe to save a card, which is also recorded as a card fact for the account's standing.
 */
function PaymentMethod() {
  const { data: settings } = useQuery(topUpSettingsQuery);
  const [removing, setRemoving] = useState(false);
  const addCard = useMutation({ mutationFn: () => startCardVerification(null) });
  if (!settings) return null;
  return (
    <Group
      label="Payment method"
      note={
        addCard.error ? (
          <span role="alert" className="text-destructive">
            {requestErrorMessage(addCard.error)}
          </span>
        ) : undefined
      }
    >
      {settings.paymentMethod ? (
        <div className="flex flex-col items-center gap-3 p-4">
          <CardPreview method={settings.paymentMethod} />
          <button
            type="button"
            className={cn(quietButton, "bg-foreground/5")}
            onClick={() => setRemoving(true)}
          >
            Remove
          </button>
          <RemovePaymentMethodDialog
            settings={settings}
            open={removing}
            onOpenChange={setRemoving}
          />
        </div>
      ) : (
        <SettingRow title="No saved card" muted>
          <button
            type="button"
            className={quietButton}
            disabled={addCard.isPending}
            onClick={() => addCard.mutate()}
          >
            Add card
          </button>
        </SettingRow>
      )}
    </Group>
  );
}

/** The saved payment method, drawn as a card: its brand or kind, and the detail that tells it apart. */
function CardPreview({ method }: { method: SavedPaymentMethod }) {
  return (
    <div className="relative flex aspect-[1.586] w-full max-w-64 flex-col justify-between rounded-2xl bg-neutral-900 p-4 text-white shadow-md ring-1 ring-white/10 dark:bg-neutral-800">
      <div className="flex items-start justify-between gap-3">
        <span className="h-6 w-8 shrink-0 rounded-md bg-gradient-to-br from-amber-200/80 to-amber-400/60" />
        <span className="truncate text-sm font-semibold tracking-widest uppercase italic">
          {method.label}
        </span>
      </div>
      {method.detail ? (
        <span className="truncate text-sm font-medium tracking-wider tabular-nums">
          {method.detail}
        </span>
      ) : null}
    </div>
  );
}
